import React, { useState, useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { AchievementsDetailProps } from './AchievementsDetail.props';
import { styled } from '@mui/material/styles';
import { toggleButtonGroupClasses } from '@mui/material/ToggleButtonGroup';
import './AchievementsDetail.css';
import ContentAchievementDetail from '../ContentAchievementDetail/ContentAchievementDetail';
import { EBadgeStatus } from '../../constants/enums';

const CustomToggleButton = styled(ToggleButton)(() => ({
  color: '#1A2929',
  fontWeight: 500,
  backgroundColor: '#ffffff',
  width: '100%',
  '& .pill': {
    display: 'inline-block',
    width: '100%',
    borderRadius: 100,
  },

  '&.Mui-selected': {
    fontWeight: 700,
    border: 0,
    backgroundColor: '#ffffff',
    '& .pill': {
      backgroundColor: '#244B77',
      color: '#fff'
    },
    '&:hover': {
      background: 'none'
    }
  },

  '&.MuiButtonBase-root': {
    fontSize: 14,
    padding: '8px'
  },
  '&:hover': {
    background: 'none'
  },
  '&.MuiToggleButton-root': {
    border: 0
  }
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(() => ({
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      border: '1px solid transparent'
    }
  },
  [`& .${toggleButtonGroupClasses.middleButton},& .${toggleButtonGroupClasses.lastButton}`]: {
    marginLeft: -1
  }
}));

const AchievementsDetail: React.FC<AchievementsDetailProps> = (props) => {
  const [selectedLockBadgeIndex, setSelectedLockBadgeIndex] = useState(null);
  const [selectedUnLockBadgeIndex, setSelectedUnLockBadgeIndex] = useState(null);
  const [valueTable, setValueTable] = React.useState<'unlocked' | 'locked'>(EBadgeStatus.unlocked);

  const { unlockedBadges, lockedBadges } = useMemo(() => {
    const allBadges = props.badgeList ?? [];
    const unlocked: any[] = [];
    const lockedWithProgress: any[] = [];
    const lockedZeroProgress: any[] = [];

    for (const badge of allBadges) {
      if (badge.status === EBadgeStatus.unlocked) {
        unlocked.push(badge); // no change for unlocked badge order
      } else {
        const progress = badge.progress_percentage ?? 0;
        if (progress > 0) lockedWithProgress.push(badge);
        else lockedZeroProgress.push(badge); // no change for locked badge with
      }
    }
    // Sort locked badges with progress_percentage (descending)
    lockedWithProgress.sort((a, b) => (b.progress_percentage ?? 0) - (a.progress_percentage ?? 0));

    return {
      unlockedBadges: unlocked,
      lockedBadges: [...lockedWithProgress, ...lockedZeroProgress]
    };
  }, [props.badgeList]);

  const handleChange = (event: any, newValue: string) => {
    if (
      newValue !== null &&
      (newValue === EBadgeStatus.unlocked || newValue === EBadgeStatus.locked)
    ) {
      setValueTable(newValue);
    }
  };

  return (
    <Box>
      <Box display={'flex'} justifyContent={'center'}>
        <StyledToggleButtonGroup
          value={valueTable}
          exclusive
          onChange={handleChange}
          aria-label="Selection"
          sx={{
            marginBottom: '30px',
            overflow: 'hidden',
            borderRadius: '50px',
            border: `1px solid #e6e7e7`,
            width: '562px',
            height: '42px',
            justifyContent: 'center'
          }}>
          <CustomToggleButton
            value={EBadgeStatus.unlocked}
            aria-label="unlocked"
            disableRipple
            disableFocusRipple
            disableTouchRipple>
            <span className="pill">Unlocked Achievements</span>
          </CustomToggleButton>

          <CustomToggleButton
            value={EBadgeStatus.locked}
            aria-label="locked"
            disableRipple
            disableFocusRipple
            disableTouchRipple>
            <span className="pill">Locked Achievements</span>
          </CustomToggleButton>
        </StyledToggleButtonGroup>
      </Box>
      {valueTable === EBadgeStatus.unlocked && (
        <ContentAchievementDetail
          listData={unlockedBadges}
          selectedBadgeIndex={selectedUnLockBadgeIndex}
          setSelectedBadgeIndex={setSelectedUnLockBadgeIndex}
          selectedBadge={
            selectedUnLockBadgeIndex !== null ? unlockedBadges[selectedUnLockBadgeIndex] : null
          }
        />
      )}
      {valueTable === EBadgeStatus.locked && (
        <ContentAchievementDetail
          listData={lockedBadges}
          selectedBadgeIndex={selectedLockBadgeIndex}
          setSelectedBadgeIndex={setSelectedLockBadgeIndex}
          selectedBadge={
            selectedLockBadgeIndex !== null ? lockedBadges[selectedLockBadgeIndex] : null
          }
        />
      )}
    </Box>
  );
};

export default AchievementsDetail;
